import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import { Animation, Asset, Layer } from '@lottie-animation-community/lottie-types';

import Monogram from './LottieAnimations/Ampersandv4.json';
import Glenarden from './LottieAnimations/Glenardenv2.json';
import Creston from './LottieAnimations/Crestonv2.json';
import Willow from './LottieAnimations/Willowv2.json';
import Locket from './LottieAnimations/Locketv3.json';
import Bridlewood from './LottieAnimations/Bridlewood.json';

dayjs.extend(utc);

const getAnimationFromKey = (animationKey: string) => {
  if (animationKey === 'overlay-animation-ampersand') {
    return Monogram;
  }
  if (animationKey === 'overlay-animation-glenarden') {
    return Glenarden;
  }
  if (animationKey === 'overlay-animation-creston') {
    return Creston;
  }
  if (animationKey === 'overlay-animation-willow') {
    return Willow;
  }
  if (animationKey === 'overlay-animation-yi-asset') {
    return Locket;
  }
  if (animationKey === 'overlay-animation-bridlewood') {
    return Bridlewood;
  }

  return Monogram;
};

interface UpdateInitialProps {
  animationKey: string;
  initialLayerOne: string;
  initialLayerTwo: string;
  color: [number, number, number, number];
  firstInitial?: string;
  secondInitial?: string;
}

export const updateInitials = (props: UpdateInitialProps) => {
  const {
    animationKey,
    initialLayerOne,
    initialLayerTwo,
    color,
    firstInitial,
    secondInitial,
  } = props;
  const animationToUse = getAnimationFromKey(animationKey) as Animation;

  const firstInitialLayer = animationToUse.layers.find(layer => layer.nm === initialLayerOne);
  if (firstInitialLayer && 't' in firstInitialLayer && firstInitialLayer.t && firstInitial) {
    // layers - an array of layers that make up the animation - https://lottiefiles.github.io/lottie-docs/layers/
    // t - the text layer (TextData)
    // d - the Animated Text Document
    // k - array of Text Document Keyframes
    // s - the Text Document (what we are trying to get to)
    // https://lottiefiles.github.io/lottie-docs/text/#text-document

    // t - the text value of t, update this value to change the text that is displayed
    // fc - fill color
    // sc - stroke color
    firstInitialLayer.t.d.k[0].s.t = firstInitial;
    firstInitialLayer.t.d.k[0].s.fc = color;
  }

  const secondInitialLayer = animationToUse.layers.find(layer => layer.nm === initialLayerTwo);

  if (secondInitialLayer && 't' in secondInitialLayer && secondInitialLayer.t && secondInitial) {
    secondInitialLayer.t.d.k[0].s.t = secondInitial;
    secondInitialLayer.t.d.k[0].s.fc = color;
  }

  return animationToUse;
};

interface UpdateInitialsForInitialsInAssetsProps extends UpdateInitialProps {
  assetIndex: number;
}

// rename this
export const updateInitialsForInitialsInAssets = (
  props: UpdateInitialsForInitialsInAssetsProps
) => {
  const {
    animationKey,
    initialLayerOne,
    initialLayerTwo,
    color,
    firstInitial,
    secondInitial,
    assetIndex,
  } = props;
  const animationToUse = getAnimationFromKey(animationKey) as Animation;

  const asset = animationToUse.assets?.[assetIndex] as Asset.Precomposition;
  const layers = asset?.layers;
  const firstInitialLayer = layers?.find(layer => layer.nm === initialLayerOne);
  if (firstInitialLayer && 't' in firstInitialLayer && firstInitialLayer.t && firstInitial) {
    firstInitialLayer.t.d.k[0].s.t = firstInitial;
    firstInitialLayer.t.d.k[0].s.fc = color;
  }

  const secondInitialLayer = layers?.find(layer => layer.nm === initialLayerTwo);

  if (secondInitialLayer && 't' in secondInitialLayer && secondInitialLayer.t && secondInitial) {
    secondInitialLayer.t.d.k[0].s.t = secondInitial;
    secondInitialLayer.t.d.k[0].s.fc = color;
  }

  return animationToUse;
};

export const customizeMonogram = (props: UpdateInitialProps) => {
  const { color, firstInitial, secondInitial } = props;

  const monogramToUse = updateInitials({
    animationKey: 'overlay-animation-ampersand',
    initialLayerOne: '.PartnerInitia_Right 2',
    initialLayerTwo: '.PartnerInitia_Left',
    color,
    firstInitial,
    secondInitial,
  });

  const { layers } = monogramToUse;

  const ampersandOne = layers.find(layer => layer.nm === 'Ampersand Half 1') as Layer.Shape;

  if (ampersandOne && ampersandOne.shapes && ampersandOne.shapes[0].ty === 'gr') {
    const shapeOne = ampersandOne.shapes[0];
    if (shapeOne.it && shapeOne.it[1].ty === 'fl') {
      shapeOne.it[1].c.k = color;
    }
  }
  const ampersandTwo = layers.find(layer => layer.nm === 'Ampersand Half 2') as Layer.Shape;

  if (ampersandTwo && ampersandTwo.shapes && ampersandTwo.shapes[0].ty === 'gr') {
    const shapeTwo = ampersandTwo.shapes[0];
    if (shapeTwo.it && shapeTwo.it[1].ty === 'fl') {
      shapeTwo.it[1].c.k = color;
    }
  }

  if (ampersandTwo && ampersandTwo.shapes && ampersandTwo.shapes[1].ty === 'gr') {
    const shapeThree = ampersandTwo.shapes[1];
    if (shapeThree.it && shapeThree.it[1].ty === 'fl') {
      shapeThree.it[1].c.k = color;
    }
  }

  return monogramToUse;
};

export const updateFillAndStrokeColors = (
  layers: Layer.Value[],
  color: [number, number, number, number]
) => {
  layers.forEach(layer => {
    if (layer.ty === 4) {
      layer.shapes.forEach(shape => {
        if (shape.ty === 'gr') {
          const fillShape = shape.it?.find(s => s.ty === 'fl');
          // not sure why type check is complaining -- added stroke.ty === 'fl' to placate it, but shouldn't be necessary
          if (fillShape && fillShape.ty === 'fl') {
            fillShape.c.k = color;
          }
          const stroke = shape.it?.find(s => s.ty === 'st');
          if (stroke && stroke.ty === 'st') {
            stroke.c.k = color;
          }
        }
      });
    }
  });
};

export const customizeCreston = (props: UpdateInitialProps) => {
  const { color, firstInitial, secondInitial } = props;

  const crestonToUse = updateInitialsForInitialsInAssets({
    animationKey: 'overlay-animation-creston',
    initialLayerOne: '.PartnerInitial2',
    initialLayerTwo: '.PartnerInitial1',
    color,
    firstInitial,
    secondInitial,
    assetIndex: 0,
  });

  const asset = crestonToUse.assets?.[1] as Asset.Precomposition;
  if (asset?.layers) {
    updateFillAndStrokeColors(asset.layers, color);
  }

  return crestonToUse;
};

export const customizeLocket = (props: UpdateInitialProps) => {
  const { color, firstInitial, secondInitial } = props;

  const locketToUse = updateInitials({
    animationKey: 'overlay-animation-yi-asset',
    initialLayerOne: '.PartnerInitial2',
    initialLayerTwo: '.PartnerInitial1',
    color,
    firstInitial,
    secondInitial,
  });

  if (locketToUse.layers) {
    updateFillAndStrokeColors(locketToUse.layers, color);
  }

  return locketToUse;
};

export const customizeBridlewood = (
  weddingDateString: string | undefined,
  color: [number, number, number, number]
) => {
  const animationToUse = Bridlewood;

  if (!weddingDateString) {
    return animationToUse;
  }
  const monthLayer = animationToUse.layers.find(layer => layer.nm === '_month');
  if (monthLayer && 't' in monthLayer && monthLayer.t) {
    const month = dayjs.utc(weddingDateString).format('MMM');
    monthLayer.t.d.k[0].s.t = month;
    monthLayer.t.d.k[0].s.fc = color;
  }
  const yearLayer = animationToUse.layers.find(layer => layer.nm === '_year');

  if (yearLayer && 't' in yearLayer && yearLayer.t) {
    const year = dayjs.utc(weddingDateString).format('YYYY');
    yearLayer.t.d.k[0].s.t = year;
    yearLayer.t.d.k[0].s.fc = color;
  }
  const dayLayer = animationToUse.layers.find(layer => layer.nm === '_day');

  if (dayLayer && 't' in dayLayer && dayLayer.t) {
    const day = dayjs.utc(weddingDateString).format('DD');

    dayLayer.t.d.k[0].s.t = day;
    dayLayer.t.d.k[0].s.fc = color;
  }
  const dividerOneLayer = animationToUse.layers.find(layer => layer.nm === 'Line 219');
  const dividerTwoLayer = animationToUse.layers.find(layer => layer.nm === 'Line 218');

  if (dividerOneLayer && dividerTwoLayer) {
    updateFillAndStrokeColors([dividerOneLayer, dividerTwoLayer] as Layer.Shape[], color);
  }

  return animationToUse;
};
